import { ProductType, POST } from "./client/types/productType"
import { INSTANCE_CLUSTER, INSTANCE_COMPANY, ProductInstanceResult, INSTANCE_STATION, InstanceType, INSTANCE_OWNERSHIP_ENTITY, INSTANCE_MARKET } from "./client/types/productInstances"
import { HierarchyKey } from "./client/types/criteria"
import { criteriaToMap } from "./client/types/filters"
import { safeIdentifier } from "./util"

export const createCompanyInstance = (product: ProductType, company: string): ProductInstanceResult | undefined => {
  if (!company?.length) { return undefined }
  const companySafe = company.toLowerCase().replace(/[^a-z0-9]/g, '')
  const productStr = (product === POST) ? 'post' : product.toLowerCase().replace(/[^a-z]/g, '')

  const name = `${company} (Company)`
  const instanceId = `company_${companySafe}_${productStr}`

  return {
    product,
    instanceId,
    instanceType: INSTANCE_COMPANY,
    name,
    criteria: [
      {
        key: 'company',
        value: company
      },
    ]
  }
}

export const createCorpParentInstance = (product: ProductType, entityName: string): (ProductInstanceResult | undefined) => {
  if (!entityName?.length) { return undefined }

  const safeProduct = safeIdentifier(product)
  const safeEntity = safeIdentifier(entityName)

  const instanceId = `ownershipentity_${safeEntity}_${safeProduct}`
  const name = `${entityName} (Owner)`

  return {
    product,
    instanceId,
    instanceType: INSTANCE_OWNERSHIP_ENTITY,
    name,
    criteria: [
      {
        key: 'corporate_owner',
        value: entityName
      },
    ],
    hierarchy: [
      { instanceType: INSTANCE_OWNERSHIP_ENTITY, instanceId }
    ]
  }
}

export const createChildEntity = (instanceType: InstanceType, parentInst: ProductInstanceResult, key: string, entityName: string, friendlyName: string): (ProductInstanceResult | undefined) => {
  if (!entityName?.length) { return undefined }
  const safeMarket = safeIdentifier(entityName)

  const product = parentInst.product
  const safeProduct = safeIdentifier(product)
  const parentCriteria = parentInst?.criteria ?? []
  const parentHier = parentInst?.hierarchy ?? []

  const prefix = parentInst.instanceId.replace(/_([a-z0-9]+)$/, "")
  const instanceId = `${prefix}_market_${safeMarket}_${safeProduct}`
  const criteria = [...parentCriteria, { key: "market", value: safeMarket! }]
  const hierarchy = [...parentHier, { instanceType: instanceType!, instanceId: instanceId! }]
  const name = friendlyName

  return {
    product,
    instanceId,
    instanceType,
    name,
    criteria,
    hierarchy,
  }
}

export const createChildClusterInstance = (parentInst: ProductInstanceResult, clusterName: string): (ProductInstanceResult | undefined) => {
  if (!clusterName?.length) { return undefined }
  return createChildEntity(INSTANCE_CLUSTER, parentInst, "cluster", clusterName, `${clusterName} (Cluster)`)
}

export const createChildMarketInstance = (parentInst: ProductInstanceResult, marketName: string): (ProductInstanceResult | undefined) => {
  if (!marketName?.length) { return undefined }
  return createChildEntity(INSTANCE_MARKET, parentInst, "market", marketName, `${marketName} (Market)`)
}

export const createClusterInstance = (product: ProductType, company: string, clusterName: string): (ProductInstanceResult | undefined) => {
  if (!company?.length || !clusterName?.length) { return undefined }

  const companySafe = company.toLowerCase().replace(/[^a-z0-9]/g, '')
  const clusterSafe = clusterName.toLowerCase().replace(/[^a-z0-9]/g, '')
  const productStr = (product === POST) ? 'post' : product.toLowerCase().replace(/[^a-z]/g, '')

  const instanceId = `company_${companySafe}_cluster_${clusterSafe}_${productStr}`

  const companyShort = company === 'iHeartMedia' ? 'IHM' : company
  // const name = `${companyShort} ${clusterName} (Cluster)`
  const name = `${clusterName} (Cluster)`

  return {
    product,
    instanceId,
    instanceType: INSTANCE_CLUSTER,
    name,
    criteria: [
      {
        key: 'company',
        value: company
      },
      {
        key: 'cluster',
        value: clusterName
      }
    ]
  }
}

export const mergeParentHierarchy = (inst: ProductInstanceResult, parent?: ProductInstanceResult) => {
  const { instanceType, instanceId } = inst
  const parentHier = parent?.hierarchy ?? []
  const hier: HierarchyKey = { instanceType: instanceType!, instanceId: instanceId! }
  inst.hierarchy = [...parentHier, hier]
  return inst
}

export const getInstanceCriteria = (inst: ProductInstanceResult, key: string) => (inst.criteria?.filter(c => c.key === key) ?? [])[0]?.value

export const getInstanceCompany = (inst: ProductInstanceResult) => {
  const company = inst?.company ?? getInstanceCriteria(inst, 'company')
  return company
}

export const getInstanceCluster = (inst: ProductInstanceResult) => {
  const cluster = getInstanceCriteria(inst, 'cluster')
  if (cluster?.length) { return cluster }

  const company = getInstanceCompany(inst)
  const region = getInstanceCriteria(inst, 'region')

  if (company === 'Independant') {
    // Place all indy stations in one cluster
    return company
  }

  if (company?.length && region?.length) {
    const companyStr = (company === 'iHeartMedia') ? 'IHM' : `${company}`
    return `${companyStr} ${region}`
  }
}

export const getInstanceCompanyInstanceId = (inst: ProductInstanceResult) => {
  const { product } = inst
  const company = getInstanceCompany(inst)
  if (!company?.length) { return undefined }

  const companySafe = company.toLowerCase().replace(/[^a-z0-9]/g, '')
  const productStr = (product === POST) ? 'post' : product.toLowerCase().replace(/[^a-z]/g, '')

  const instanceId = `company_${companySafe}_${productStr}`
  return instanceId
}

export const getInstanceClusterInstanceId = (inst: ProductInstanceResult) => {
  const { product } = inst
  const company = getInstanceCompany(inst)
  const clusterName = getInstanceCluster(inst)
  if (!company?.length || !clusterName?.length) { return undefined }

  const companySafe = company?.toLowerCase().replace(/[^a-z0-9]/g, '')
  const clusterSafe = clusterName?.toLowerCase().replace(/[^a-z0-9]/g, '')
  const productStr = (product === POST) ? 'post' : product.toLowerCase().replace(/[^a-z]/g, '')

  const instanceId = `company_${companySafe}_cluster_${clusterSafe}_${productStr}`
  return instanceId
}

export const getInstanceStationInstanceId = (inst: ProductInstanceResult) => {
  if (inst.instanceType === INSTANCE_STATION) {
    return inst.instanceId
  }

  return undefined
}

export const getInstanceHierarchyInstanceId = (inst: ProductInstanceResult, instanceType: InstanceType) => {
  const val = inst?.hierarchy?.filter(h => h.instanceType === instanceType)[0]?.instanceId
  return val
}

/**
 * Matches instances by any of `company`, `cluster`, `name`
 * @param instances 
 * @param criteria 
 */
export function matchInstances(arr: ProductInstanceResult[], search: RegExp) {
  return arr.filter(inst => {
    const map = criteriaToMap(inst, (s) => s.toLowerCase().replace(/[^a-z]/g, ''))
    const company = (map.get('company') ?? "").toLowerCase().replace(/[^a-z]/g, '')
    const cluster = (map.get('cluster') ?? "").toLowerCase().replace(/[^a-z]/g, '')
    const name = inst.name

    if (search.exec(company ?? "") || search.exec(cluster ?? "") || search.exec(name ?? "")) {
      return true
    }

    return false
  })
}

export function matchInstancesByPrefix(arr: ProductInstanceResult[], search: string) {
  const searchStr = search?.toLowerCase()?.replace(/[^a-z]/g, '')
  if (!searchStr?.length) { return arr }
  const regex = new RegExp(`^${searchStr}.*`)
  return matchInstances(arr, regex)
}

const ABS_URL = /^(http|https):\/\//
export const makeIcon = (url: string) => {
  if (!url?.length) {
    return undefined
  } else if (ABS_URL.exec(url)) {
    return url
  } else {
    return `https://post.futurimedia.com/${url}`
  }
}
